// Torre de control de flota: seguimiento en vivo de los repartos (posición en mapa,
// progreso, ETA heurístico, retrasos). Catering → Logística → Torre de control.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Radar, Truck, MapPin, Clock, AlertTriangle, WifiOff, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { num } from '@/features/inventario/shared';
import { catRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

// Fix iconos de Leaflet en bundler (igual que FormWidgets)
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Prox { cliente: string; direccion: string | null; hora_objetivo: string | null }
interface Pos { lat: number; lon: number; hora: string }
interface DespF {
  despacho_id: string; codigo: string; estado: string; ruta: string | null; placa: string | null; chofer: string | null; vehiculo: string | null;
  total: number; entregadas: number; incidencias: number; pendientes: number; retrasos: number;
  proxima: Prox | null; pos: Pos | null; min_sin_senal: number | null; eta_fin: string | null; semaforo: string;
}
interface Tablero { fecha: string; despachos: DespF[]; resumen: { en_ruta: number; atrasados: number; sin_senal: number; completados: number } }

const SEM: Record<string, { cls: string; txt: string; label: string }> = {
  en_ruta: { cls: 'border-sky-400', txt: 'text-sky-600', label: 'En ruta' },
  atrasado: { cls: 'border-red-500', txt: 'text-red-600', label: 'Atrasado' },
  sin_senal: { cls: 'border-amber-500', txt: 'text-amber-600', label: 'Sin señal' },
  completado: { cls: 'border-emerald-500', txt: 'text-emerald-600', label: 'Completado' },
};
const hhmm = (s: string | null): string => s ? new Date(s.length <= 8 ? `2000-01-01T${s}` : s).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' }) : '—';

export function FlotaCatering() {
  const { data, isLoading } = useQuery<Tablero>({
    queryKey: ['cat-flota'],
    queryFn: () => catRpc<Tablero>('cat_flota_tablero', {}),
    refetchInterval: 15000,
  });
  const conPos = useMemo(() => (data?.despachos ?? []).filter((d) => d.pos), [data]);
  const center = conPos[0]?.pos ? [conPos[0].pos!.lat, conPos[0].pos!.lon] as [number, number] : [-2.170998, -79.922359] as [number, number];
  const r = data?.resumen;

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Radar className="h-4 w-4" /> Torre de control de flota</CardTitle>
          <p className="text-xs text-muted-foreground">Repartos del día en vivo · se actualiza cada 15 s.</p>
        </CardHeader>
        <CardContent>
          {r && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">En ruta</p><p className="text-xl font-semibold text-sky-600">{num(r.en_ruta, 0)}</p></div>
              <div className={`rounded-md border p-2 ${r.atrasados > 0 ? 'border-red-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Atrasados</p><p className={`text-xl font-semibold ${r.atrasados > 0 ? 'text-red-600' : ''}`}>{num(r.atrasados, 0)}</p></div>
              <div className={`rounded-md border p-2 ${r.sin_senal > 0 ? 'border-amber-400' : ''}`}><p className="text-[11px] uppercase text-muted-foreground">Sin señal</p><p className={`text-xl font-semibold ${r.sin_senal > 0 ? 'text-amber-600' : ''}`}>{num(r.sin_senal, 0)}</p></div>
              <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Completados</p><p className="text-xl font-semibold text-emerald-600">{num(r.completados, 0)}</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        {/* Mapa */}
        <Card>
          <CardContent className="p-0">
            <div className="h-[26rem] w-full overflow-hidden rounded-lg">
              <MapContainer center={center} zoom={12} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {conPos.map((d) => (
                  <Marker key={d.despacho_id} position={[d.pos!.lat, d.pos!.lon]}>
                    <Popup>
                      <b>{d.codigo}</b> · {d.ruta}<br />
                      {d.entregadas}/{d.total} entregas{d.retrasos > 0 ? ` · ${d.retrasos} atrasada(s)` : ''}<br />
                      {d.proxima ? `Próxima: ${d.proxima.cliente}` : 'Sin pendientes'}<br />
                      <span className="text-muted-foreground">Señal hace {d.min_sin_senal ?? '?'} min</span>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tablero */}
        <div className="space-y-2">
          {isLoading && <p className="py-4 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
          {(data?.despachos ?? []).map((d) => {
            const s = SEM[d.semaforo] ?? SEM.en_ruta;
            const pct = d.total > 0 ? Math.round((100 * d.entregadas) / d.total) : 0;
            return (
              <div key={d.despacho_id} className={`rounded-lg border-l-4 bg-card p-3 shadow-sm ${s.cls}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{d.codigo}</span>
                    <span className="text-xs text-muted-foreground">{d.ruta}{d.placa ? ` · ${d.placa}` : ''}</span></div>
                  <span className={`flex items-center gap-1 text-xs font-semibold ${s.txt}`}>
                    {d.semaforo === 'atrasado' && <AlertTriangle className="h-3.5 w-3.5" />}
                    {d.semaforo === 'sin_senal' && <WifiOff className="h-3.5 w-3.5" />}
                    {d.semaforo === 'completado' && <CheckCircle2 className="h-3.5 w-3.5" />}
                    {s.label}
                  </span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  <span>{d.entregadas}/{d.total} entregas{d.incidencias > 0 ? ` · ${d.incidencias} incid.` : ''}</span>
                  {d.proxima && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.proxima.cliente}{d.proxima.hora_objetivo ? ` (${hhmm(d.proxima.hora_objetivo)})` : ''}</span>}
                  {d.retrasos > 0 && <span className="text-red-600">{d.retrasos} atrasada(s)</span>}
                  {d.eta_fin && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> ETA {hhmm(d.eta_fin)}</span>}
                  <span>{d.pos ? `señal ${d.min_sin_senal} min` : 'sin posición'}</span>
                </div>
              </div>
            );
          })}
          {data && !data.despachos.length && <Card><CardContent className="py-6 text-sm text-muted-foreground">No hay repartos en ruta hoy.</CardContent></Card>}
        </div>
      </div>
    </div>
  );
}
